/**
 * Frontend log capture — intercepts console.log/warn/error and stores
 * entries in an in-memory ring buffer.  Admin users can flush the buffer
 * to the backend via POST /admin/logs/frontend.
 */
import logService, { LogEntry } from './logService';

const MAX_BUFFER = 200;
const _buffer: LogEntry[] = [];

const _origLog = console.log;
const _origWarn = console.warn;
const _origError = console.error;

function _capture(level: string, args: unknown[]) {
  const message = args
    .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
    .join(' ');
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    source: 'frontend',
  };
  _buffer.push(entry);
  if (_buffer.length > MAX_BUFFER) {
    _buffer.shift();
  }
}

/** Install console interceptors. Call once at app startup. */
function install() {
  console.log = (...args: unknown[]) => {
    _capture('INFO', args);
    _origLog.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    _capture('WARNING', args);
    _origWarn.apply(console, args);
  };
  console.error = (...args: unknown[]) => {
    _capture('ERROR', args);
    _origError.apply(console, args);
  };
}

/** Return a snapshot of the current buffer (most recent last). */
function getBuffer(): LogEntry[] {
  return [..._buffer];
}

/** Flush the buffer to the backend and clear it. */
async function flush(): Promise<void> {
  if (_buffer.length === 0) return;
  const batch = [..._buffer];
  _buffer.length = 0;
  try {
    await logService.postFrontendLogs(batch);
  } catch {
    // Re-add entries on failure so they aren't lost
    _buffer.push(...batch);
  }
}

export default { install, getBuffer, flush };
